import { createContext, useCallback, useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

export const ConversationsContext = createContext();

export const ConversationsProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  // Store current conversation info from chat init API (for cases when conversation isn't in list yet)
  const [currentConversationInfo, setCurrentConversationInfo] = useState(null);
  // Pending navigation data - stores both path and conversation info
  const pendingNavigationRef = useRef(null);

  const refreshConversations = useCallback(() => {
    const token = Cookies.get("chatToken");

    if (!token) {
      setLoading(false);
      return;
    }

    // Mark loading before the authenticated fetch. On first login this callback
    // first ran pre-login (no token -> loading=false); without resetting it here
    // the post-login fetch runs with loading=false, so Chat's bare-/chat branch
    // sees conversations=[] && !loading and flashes the error screen for the ~1s
    // the list takes to load. Keeping loading=true shows the skeleton instead.
    setLoading(true);

    axios
      .get(`https://aichatbot-326159028339.asia-southeast1.run.app/chat/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((data) => {
        const conversationsData = data.data?.data || [];
        setConversations(conversationsData);

        if (conversationsData.length > 0 && location.pathname === "/chat") {
          // API returns data sorted by createdAt descending (newest first)
          // Use first conversation (newest)
          const firstConversation = conversationsData[0];
          const newCId = firstConversation?.companyId;
          const newJId = firstConversation?.jobId;

          if (newCId && newJId) {
            // Store navigation data in ref for immediate use
            pendingNavigationRef.current = {
              path: `/chat/${newCId}/${newJId}`,
              conversationInfo: {
                companyId: newCId,
                jobId: newJId,
                companyName: firstConversation.companyName,
                companyPhoto: firstConversation.companyPhoto,
                aichatbotAvatarUrl: firstConversation.aichatbotAvatarUrl,
                jobName: firstConversation.jobName,
                status: firstConversation.status,
              }
            };

            // Set current conversation info BEFORE navigation so it's available immediately
            setCurrentConversationInfo(pendingNavigationRef.current.conversationInfo);

            // Navigate with state containing conversation info
            navigate(pendingNavigationRef.current.path, {
              state: { conversationInfo: pendingNavigationRef.current.conversationInfo }
            });
            pendingNavigationRef.current = null;
          }
        }

        setLoading(false);
      })
      .catch((err) => {
        // If token is expired/invalid (401), clear it and redirect to login
        if (err.response?.status === 401) {
          Cookies.remove("chatToken");
          navigate("/login", { replace: true });
          return;
        }
        setLoading(false);
      });
  }, [location.pathname, navigate]);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  return (
    <ConversationsContext.Provider
      value={{ conversations, refreshConversations, loading, currentConversationInfo, setCurrentConversationInfo }}
    >
      {children}
    </ConversationsContext.Provider>
  );
};
